import {
  EuiComboBox,
  EuiComboBoxOptionOption,
  EuiForm,
  EuiFormRow,
  EuiSpacer,
  EuiText,
  EuiTitle,
} from "@elastic/eui";
import { useEffect, useMemo, useState } from "react";
import {
  AnketaResourcesEnum,
  isValidEmail,
  KeycloakGroup,
  KeycloakUser,
  useAnketaAuth,
  useAnketaI18n,
  useApi,
  useToast,
} from "../../../";
import { OpsanioCreateContactDefaults } from "../../";

interface SelectContactsProps {
  contacts: OpsanioCreateContactDefaults;
  setContacts: React.Dispatch<
    React.SetStateAction<OpsanioCreateContactDefaults>
  >;
}

const userLabel = (user: KeycloakUser): string =>
  `${user.firstName || ""} ${user.lastName || ""} (${user.email})`.trim();

/**
 * Wizard step that captures the General-section contacts at creation time.
 * The opsanio (internal) sales/technical contacts are picked from the
 * corresponding Keycloak contact groups (resolved to user ids); the customer
 * contacts are free-typed e-mail addresses and are optional - the step never
 * blocks the wizard. The selections are seeded into the questionnaire's contact
 * facts by the creation workflow (see create/creation/index.tsx).
 */
export const SelectContacts = ({
  contacts,
  setContacts,
}: SelectContactsProps): JSX.Element => {
  const { keycloak } = useAnketaAuth();
  const { Get } = useApi();
  const { addToast } = useToast();
  const { translate } = useAnketaI18n();

  const [salesUsers, setSalesUsers] = useState<KeycloakUser[]>([]);
  const [technicalUsers, setTechnicalUsers] = useState<KeycloakUser[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const fetchGroup = async (
    type: "sales" | "technical",
  ): Promise<KeycloakUser[]> => {
    try {
      const { data } = await Get({
        path: `v2/${AnketaResourcesEnum.opsanio}/keycloak/groups/${type}`,
        responseType: "json",
        token: keycloak.token,
      });
      const group = (data as Array<KeycloakGroup>)?.[0];
      return group?.members ?? [];
    } catch (error) {
      console.error(`Error fetching ${type} contact group:`, error);
      return [];
    }
  };

  useEffect(() => {
    if (!keycloak || typeof keycloak.token !== "string") return;
    setIsLoading(true);
    Promise.all([fetchGroup("sales"), fetchGroup("technical")])
      .then(([sales, technical]) => {
        setSalesUsers(sales);
        setTechnicalUsers(technical);
      })
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keycloak]);

  const salesOptions = useMemo<EuiComboBoxOptionOption<KeycloakUser>[]>(
    () => salesUsers.map((user) => ({ label: userLabel(user), value: user })),
    [salesUsers],
  );
  const technicalOptions = useMemo<EuiComboBoxOptionOption<KeycloakUser>[]>(
    () =>
      technicalUsers.map((user) => ({ label: userLabel(user), value: user })),
    [technicalUsers],
  );

  const selectedFor = (
    users: KeycloakUser[],
    ids: string[],
  ): EuiComboBoxOptionOption<KeycloakUser>[] =>
    ids
      .map((id) => users.find((u) => u.id === id))
      .filter((u): u is KeycloakUser => Boolean(u))
      .map((u) => ({ label: userLabel(u), value: u }));

  const emailOptionsFor = (
    emails: string[],
  ): EuiComboBoxOptionOption[] => emails.map((email) => ({ label: email }));

  const onCreateEmail = (
    key:
      | "customerSales"
      | "customerTechnical"
      | "customerOrganizational",
    searchValue: string,
  ): void => {
    const email = searchValue.trim();
    if (!isValidEmail(email)) {
      addToast({
        title:
          translate("anketa.contacts.customer.invalidEmail.title", {
            defaultMessage: "Invalid e-mail",
          }) || "Invalid e-mail",
        color: "danger",
        text:
          translate("anketa.contacts.customer.invalidEmail.text", {
            defaultMessage: "Please enter a valid e-mail address.",
          }) || "Please enter a valid e-mail address.",
      });
      return;
    }
    setContacts((prev) =>
      prev[key].includes(email)
        ? prev
        : { ...prev, [key]: [...prev[key], email] },
    );
  };

  const customerRoles: {
    key: "customerSales" | "customerTechnical" | "customerOrganizational";
    label: string;
  }[] = [
    {
      key: "customerSales",
      label:
        translate("anketa.contacts.customer.sales", {
          defaultMessage: "Customer - Sales contact",
        }) || "Customer - Sales contact",
    },
    {
      key: "customerTechnical",
      label:
        translate("anketa.contacts.customer.technical", {
          defaultMessage: "Customer - Technical contact",
        }) || "Customer - Technical contact",
    },
    {
      key: "customerOrganizational",
      label:
        translate("anketa.contacts.customer.organizational", {
          defaultMessage: "Customer - Organizational contact",
        }) || "Customer - Organizational contact",
    },
  ];

  return (
    <EuiForm>
      <EuiTitle size="xs">
        <h3>
          {translate("anketa.contacts.opsanio", {
            defaultMessage: "opsanio contacts",
          }) || "opsanio contacts"}
        </h3>
      </EuiTitle>
      <EuiText size="xs" color="subdued">
        <p>
          {translate("anketa.contacts.opsanio.helper", {
            defaultMessage:
              "Internal sales and technical contacts for this deployment.",
          }) ||
            "Internal sales and technical contacts for this deployment."}
        </p>
      </EuiText>
      <EuiSpacer size="s" />

      <EuiFormRow
        label={
          translate("anketa.documentation.opsanio.general.sales", {
            defaultMessage: "Sales contact",
          }) || "Sales contact"
        }
        fullWidth
      >
        <EuiComboBox
          placeholder={
            translate("anketa.contacts.opsanio.sales.placeholder", {
              defaultMessage: "Select sales contacts",
            }) || "Select sales contacts"
          }
          options={salesOptions}
          selectedOptions={selectedFor(salesUsers, contacts.sales)}
          onChange={(selected) =>
            setContacts((prev) => ({
              ...prev,
              sales: selected.map((o) => o.value!.id),
            }))
          }
          isLoading={isLoading}
          isClearable
          fullWidth
        />
      </EuiFormRow>

      <EuiFormRow
        label={
          translate(
            "anketa.documentation.opsanio.general.technical_contact",
            { defaultMessage: "Technical contact" },
          ) || "Technical contact"
        }
        fullWidth
      >
        <EuiComboBox
          placeholder={
            translate("anketa.contacts.opsanio.technical.placeholder", {
              defaultMessage: "Select technical contacts",
            }) || "Select technical contacts"
          }
          options={technicalOptions}
          selectedOptions={selectedFor(technicalUsers, contacts.technical)}
          onChange={(selected) =>
            setContacts((prev) => ({
              ...prev,
              technical: selected.map((o) => o.value!.id),
            }))
          }
          isLoading={isLoading}
          isClearable
          fullWidth
        />
      </EuiFormRow>

      <EuiSpacer size="l" />

      <EuiTitle size="xs">
        <h3>
          {translate("anketa.contacts.customer", {
            defaultMessage: "Customer contacts",
          }) || "Customer contacts"}
        </h3>
      </EuiTitle>
      <EuiText size="xs" color="subdued">
        <p>
          {translate("anketa.contacts.customer.helper", {
            defaultMessage:
              "Optional. Type customer contact e-mail addresses; they may be left empty and completed later.",
          }) ||
            "Optional. Type customer contact e-mail addresses; they may be left empty and completed later."}
        </p>
      </EuiText>
      <EuiSpacer size="s" />

      {customerRoles.map((role) => (
        <EuiFormRow key={role.key} label={role.label} fullWidth>
          <EuiComboBox
            noSuggestions
            placeholder={
              translate("anketa.contacts.customer.placeholder", {
                defaultMessage: "Add e-mail address",
              }) || "Add e-mail address"
            }
            selectedOptions={emailOptionsFor(contacts[role.key])}
            onCreateOption={(searchValue) =>
              onCreateEmail(role.key, searchValue)
            }
            onChange={(selected) =>
              setContacts((prev) => ({
                ...prev,
                [role.key]: selected.map((o) => o.label),
              }))
            }
            isClearable
            fullWidth
          />
        </EuiFormRow>
      ))}
    </EuiForm>
  );
};
